import { useEffect, useState, useCallback } from "react";
import { Store, MapPin, Package, AlertCircle, Loader2 } from "lucide-react";
import * as bazaar from "@/api/bazaar";
import type { BazaarEntrance, HaatSummary, StallOption } from "@/api/bazaar";

// ═══════════════════════════════════════════════════════════════════
// The Bazaar lab.
//
// One page rather than three, because the point of this surface is to make the
// whole commerce loop testable in one place: what is near me, what is on the
// shelves, which of my businesses can take a stall, and what happens when one
// does. Splitting it would mean clicking between pages to see whether a rule
// fired.
//
// Everything it renders comes from the API's own answers. In particular it
// does NOT re-sort stalls (they arrive shuffled per request, and re-sorting
// client-side would quietly recreate the front row the server refuses to sell)
// and it does NOT hide empty shelves (a shelf with nothing on it is where a
// business in that trade learns it belongs here).
// ═══════════════════════════════════════════════════════════════════

export default function BazaarIndex() {
  const [entrance, setEntrance] = useState<BazaarEntrance | null>(null);
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (override?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await bazaar.getEntrance(override ? { city: override } : undefined);
      setEntrance(res.data);
      setCity(res.data.city ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the Bazaar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !entrance) {
    return (
      <div className="flex items-center gap-2 text-gray-500 p-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the Bazaar…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <Store className="w-6 h-6 text-orange-600" /> The Bazaar
        </h1>
        <p className="text-sm text-gray-600">
          One call answers both halves — open haats above, the shelves of shops near you below.
          It takes a <strong>city</strong>, not a metro code, and resolves the metro once so the
          shelf counts and the directory cannot disagree.
        </p>
      </header>

      {/* Who am I asking as — the whole surface pivots on this. */}
      <section className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-gray-500 mb-1">Ask from this city</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="San Jose"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-56"
            />
          </label>
          <button
            onClick={() => void load(city.trim() || undefined)}
            className="bg-gray-900 text-white text-sm rounded px-4 py-1.5"
          >
            Look
          </button>
          <button
            onClick={() => void load()}
            className="text-sm text-gray-600 underline"
            title="Clear the override and use the signed-in member's own city"
          >
            use my own city
          </button>
        </div>

        <div className="text-sm text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
          <span>
            <MapPin className="w-3.5 h-3.5 inline mr-1" />
            resolved to:{" "}
            <strong>{entrance?.metro ? entrance.metro.name : "no metro"}</strong>
            {!entrance?.metro && (
              <span className="text-gray-500">
                {" "}
                — not an error, the answer is national rather than local
              </span>
            )}
          </span>
          <span>
            <Package className="w-3.5 h-3.5 inline mr-1" />
            {entrance?.shopCount ?? 0} shops across {entrance?.shelves.length ?? 0} shelves
          </span>
        </div>
      </section>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <Haats haats={entrance?.haats ?? []} />
      <Shelves entrance={entrance} />
    </div>
  );
}

// ─── Haats, each with its own self-serve panel ──────────────────────

function Haats({ haats }: { haats: HaatSummary[] }) {
  return (
    <section className="space-y-3">
      <h2 className="font-semibold">Haats in this metro</h2>
      {haats.length === 0 && (
        <p className="text-sm text-gray-500">
          No open or upcoming haat here. A haat opens by its own clock — nothing needs to run.
        </p>
      )}
      <div className="space-y-3">
        {haats.map((h) => (
          <HaatCard key={h.id} haat={h} />
        ))}
      </div>
    </section>
  );
}

function HaatCard({ haat }: { haat: HaatSummary }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-start justify-between gap-4"
      >
        <div>
          <div className="font-medium">{haat.name}</div>
          <div className="text-sm text-gray-600 mt-0.5">
            {haat.occasion ?? "—"} · {haat.stallCount} stall
            {haat.stallCount === 1 ? "" : "s"} ·{" "}
            {new Date(haat.opensAt).toLocaleDateString()} –{" "}
            {new Date(haat.closesAt).toLocaleDateString()}
          </div>
          {haat.stallsFree && (
            <div className="text-xs text-green-700 mt-1">stalls are free this season</div>
          )}
          {haat.acceptedCategories.length > 0 ? (
            <div className="text-xs text-gray-500 mt-1">
              accepts: {haat.acceptedCategories.join(", ")}
            </div>
          ) : (
            <div className="text-xs text-gray-500 mt-1">
              accepts any eligible trade (empty list = no curation)
            </div>
          )}
        </div>
        <StatusPill status={haat.status} />
      </button>
      {open && <SelfServe haat={haat} />}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  // Open string on the wire. An unknown value renders rather than blanking.
  const tone =
    status === "open"
      ? "bg-green-100 text-green-800"
      : status === "upcoming"
        ? "bg-amber-100 text-amber-800"
        : "bg-gray-100 text-gray-700";
  return <span className={`text-xs px-2 py-1 rounded shrink-0 ${tone}`}>{status}</span>;
}

/**
 * The self-serve loop, per haat.
 *
 * Asks the API what the signed-in member's businesses could do here BEFORE
 * offering any button, which is the whole point of `my-stalls`: a shop owner
 * who taps and fails learns nothing, and a refusal that names the rule tells
 * them whether to fix something or wait for a different haat.
 */
function SelfServe({ haat }: { haat: HaatSummary }) {
  const [options, setOptions] = useState<StallOption[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await bazaar.myStallOptions(haat.id);
      setOptions(res.data.businesses);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load your businesses");
    }
  }, [haat.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, id: string, ok: string) => {
    setBusy(id);
    setNote(null);
    setErr(null);
    try {
      await fn();
      setNote(ok || null);
      await load();
    } catch (e) {
      // The refusals ARE the feature — show the server's own words rather than
      // a generic failure, because the message names which rule fired.
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
      <h3 className="text-sm font-medium">My businesses here</h3>

      {options === null && <p className="text-sm text-gray-500">Checking…</p>}
      {options?.length === 0 && (
        <p className="text-sm text-gray-500">
          You have not claimed any listing. Only a claimed business can take a stall.
        </p>
      )}

      {options?.map((b) => (
        <div
          key={b.providerId}
          className="bg-white border border-gray-200 rounded p-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <div className="font-medium text-sm">{b.name}</div>
            <div className="text-xs text-gray-600">
              {b.city}
              {b.reason && (
                <>
                  {" · "}
                  <span className="text-amber-700">{b.reason}</span>
                  {b.reasonCode && (
                    <span className="text-gray-400"> ({b.reasonCode})</span>
                  )}
                </>
              )}
              {b.canTakeStall && b.feeWaived && (
                <span className="text-green-700"> · free, first stall</span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {b.canTakeStall && (
              <button
                disabled={busy === b.providerId}
                onClick={() =>
                  act(
                    () => bazaar.rentStall(haat.id, { providerId: b.providerId }),
                    b.providerId,
                    `${b.name} has a stall.`,
                  )
                }
                className="text-sm bg-gray-900 text-white rounded px-3 py-1.5 disabled:opacity-50"
              >
                Take a stall
              </button>
            )}
            {b.stallId && (
              <button
                disabled={busy === b.providerId}
                onClick={() => {
                  // Empty takes the line off; Cancel changes nothing.
                  const line = window.prompt("The stall's line (empty takes it off)");
                  if (line === null) return;
                  act(
                    () => bazaar.setStallHeadline(b.stallId as string, line.trim() || null),
                    b.providerId,
                    `${b.name}'s stall line is saved.`,
                  );
                }}
                className="text-sm border border-gray-300 rounded px-3 py-1.5 disabled:opacity-50"
              >
                Change line
              </button>
            )}
            {b.stallId && (
              <button
                disabled={busy === b.providerId}
                onClick={() =>
                  act(
                    async () => {
                      const { data } = await bazaar.getStallSummary(b.stallId as string);
                      const c = data.counts;
                      setSummary(
                        `${data.haat.name} (${data.haat.status}): ${c.pageViews} page visits, ` +
                          `${c.contactTaps} taps, ${c.conversations} conversations from the haat` +
                          (data.nextHaat ? `. Next: ${data.nextHaat.name}.` : "."),
                      );
                    },
                    b.providerId,
                    "",
                  )
                }
                className="text-sm border border-gray-300 rounded px-3 py-1.5 disabled:opacity-50"
              >
                How it went
              </button>
            )}
            {b.stallId && (
              <button
                disabled={busy === b.providerId}
                onClick={() =>
                  act(
                    () => bazaar.releaseStall(b.stallId as string),
                    b.providerId,
                    `${b.name} gave up its stall. Taking it back here is not a second stall.`,
                  )
                }
                className="text-sm border border-gray-300 rounded px-3 py-1.5 disabled:opacity-50"
              >
                Give it up
              </button>
            )}
          </div>
        </div>
      ))}

      {note && <p className="text-sm text-green-700">{note}</p>}
      {summary && <p className="text-sm text-gray-700">{summary}</p>}
      {err && <p className="text-sm text-red-700">{err}</p>}
    </div>
  );
}

// ─── Shelves ────────────────────────────────────────────────────────

function Shelves({ entrance }: { entrance: BazaarEntrance | null }) {
  const shelves = entrance?.shelves ?? [];
  const empty = shelves.filter((s) => s.shopCount === 0);

  return (
    <section className="space-y-3">
      <h2 className="font-semibold">Shelves</h2>
      <p className="text-sm text-gray-600">
        Every trade that may hold a stall, in reference-data order — deliberately not sorted by
        supply, which would bury the shelves that most need businesses.
        {empty.length > 0 && (
          <>
            {" "}
            <strong>{empty.length}</strong> {empty.length === 1 ? "is" : "are"} empty, and shown
            on purpose.
          </>
        )}
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {shelves.map((s) => (
          <div
            key={s.code}
            className={`border rounded p-3 ${
              s.shopCount === 0 ? "border-dashed border-gray-300 bg-gray-50" : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-sm">{s.name}</span>
              <span
                className={`text-sm tabular-nums ${
                  s.shopCount === 0 ? "text-gray-400" : "text-gray-900"
                }`}
              >
                {s.shopCount}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {s.code} · {s.kind}
            </div>
            {entrance?.metro && (
              <a
                href={`/lab/providers?category=${s.code}&metro=${entrance.metro.code}`}
                className="text-xs text-blue-700 underline mt-2 inline-block"
              >
                open the directory with the same rule
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
